import React from "react";
import { Link } from "react-router-dom";
import { Facebook, Mail, MapPin, Phone, Youtube } from "lucide-react";
import { useSiteConfig } from "../lib/siteConfig";
import { LOCATIONS } from "../data/locations";
import { PRIORITY_CITY_SLUGS } from "../data/localHireContent";
import { TEMPO_CITY_SLUGS } from "../data/cityContent";
import { VEHICLES } from "../data/vehicles";
import { WhatsAppIcon } from "./Header";
import { openWhatsApp, defaultWhatsAppMessage } from "../lib/whatsapp";
import { FooterDeveloperCredit } from "./FooterDeveloperCredit";
import { assetUrl } from "../lib/buildSnapshot";
import { taxiCityPath } from "../data/publicRoutes";

export default function Footer({ informationOnly = false }) {
    const config = useSiteConfig();

    return (
        <footer data-testid="site-footer" className="mt-24 bg-brand-navy text-white relative overflow-hidden">
            <div className="absolute inset-0 grid-lines opacity-10 pointer-events-none" />
            <div className="absolute inset-0 noise-overlay pointer-events-none" />

            <div className="container-x relative z-10 py-16 md:py-20">
                <div className="grid gap-12 md:grid-cols-2 lg:grid-cols-4">
                    <div>
                        <div className="flex items-center gap-2.5">
                            <img
                                src={assetUrl("/brand/logo-56.webp?v=badge-e75c0e4c89f4")}
                                srcSet={[56, 112, 168].map((size, i) => `${assetUrl(`/brand/logo-${size}.webp?v=badge-e75c0e4c89f4`)} ${i + 1}x`).join(", ")}
                                alt="VVPSS Taxi Services logo"
                                width="44"
                                height="44"
                                className="h-11 w-11 shrink-0 rounded-lg bg-white/90 object-contain p-1"
                                loading="lazy"
                                decoding="async"
                            />
                            <div>
                                <div className="font-display text-lg font-extrabold">
                                    VVPSS Taxi Services
                                </div>
                                <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-white/60">
                                    Pvt. Ltd. · Satna
                                </div>
                            </div>
                        </div>
                        <p className="mt-5 text-sm text-white/70 leading-relaxed">
                            {informationOnly ? "VVPSS Pvt. Ltd. Taxi Services has its corporate office in Satna. The tourist directory is for travel information; destination listings do not confirm local branches, fares or route availability." : "Taxi, cab booking, car hire and Tempo Traveller rental across all listed service locations. Every listed service and vehicle option is offered in every listed location. Corporate office: Satna."}
                        </p>
                        <div className="mt-5 space-y-2 text-sm text-white/85">
                            <a
                                data-testid="footer-phone-link"
                                href={`tel:${config.phone}`}
                                className="flex items-center gap-2 hover:text-brand-gold"
                            >
                                <Phone className="h-4 w-4 text-brand-gold" />
                                {config.phone_display}
                            </a>
                            {config.phone_alt_1 && (
                                <a
                                    data-testid="footer-phone-alt-1-link"
                                    href={`tel:${config.phone_alt_1.replace(/\s/g, "")}`}
                                    className="flex items-center gap-2 hover:text-brand-gold"
                                >
                                    <Phone className="h-4 w-4 text-brand-gold" />
                                    {config.phone_alt_1}
                                </a>
                            )}
                            {config.phone_alt_2 && (
                                <a
                                    data-testid="footer-phone-alt-2-link"
                                    href={`tel:${config.phone_alt_2.replace(/\s/g, "")}`}
                                    className="flex items-center gap-2 hover:text-brand-gold"
                                >
                                    <Phone className="h-4 w-4 text-brand-gold" />
                                    {config.phone_alt_2}
                                </a>
                            )}
                            <a
                                data-testid="footer-email-link"
                                href={`mailto:${config.email}`}
                                className="flex items-center gap-2 hover:text-brand-gold"
                            >
                                <Mail className="h-4 w-4 text-brand-gold" />
                                {config.email}
                            </a>
                            <div className="flex items-start gap-2">
                                <MapPin className="mt-0.5 h-4 w-4 flex-shrink-0 text-brand-gold" />
                                <span data-testid="footer-address">Corporate Office: {config.address}</span>
                            </div>
                        </div>

                        <div className="mt-5 flex items-center gap-3">
                            {config.facebook_url && (
                                <a
                                    data-testid="footer-facebook-link"
                                    href={config.facebook_url}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-white/15 text-white/85 hover:border-brand-gold hover:text-brand-gold"
                                    aria-label="Facebook"
                                >
                                    <Facebook className="h-4 w-4" />
                                </a>
                            )}
                            {config.youtube_url && (
                                <a
                                    data-testid="footer-youtube-link"
                                    href={config.youtube_url}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-white/15 text-white/85 hover:border-brand-gold hover:text-brand-gold"
                                    aria-label="YouTube"
                                >
                                    <Youtube className="h-4 w-4" />
                                </a>
                            )}
                        </div>
                    </div>

                    <div>
                        <h4 className="label-eyebrow text-brand-gold">
                            Company
                        </h4>
                        <ul className="mt-4 space-y-2 text-sm text-white/80">
                            <li><Link data-testid="footer-home-link" to="/" className="hover:text-brand-gold">Home</Link></li>
                            <li><Link data-testid="footer-about-link" to="/about" className="hover:text-brand-gold">About Us</Link></li>
                            <li><Link data-testid="footer-fleet-link" to="/fleet" className="hover:text-brand-gold">Our Fleet</Link></li>
                            <li><Link data-testid="footer-services-link" to="/services" className="hover:text-brand-gold">Services</Link></li>
                            <li><Link data-testid="footer-outstation-link" to="/outstation" className="hover:text-brand-gold">Outstation Packages</Link></li>
                            <li><Link data-testid="footer-airport-transfer-link" to="/airport-transfer" className="hover:text-brand-gold">Airport Transfer</Link></li>
                            <li><Link data-testid="footer-railway-transfer-link" to="/railway-transfer" className="hover:text-brand-gold">Railway Transfer</Link></li>
                            <li><Link data-testid="footer-tempo-traveller-link" to="/tempo-traveller-rental" className="hover:text-brand-gold">Tempo Traveller Rental</Link></li>
                            <li><Link data-testid="footer-compare-link" to="/compare" className="hover:text-brand-gold">Compare Fleet</Link></li>
                            <li><Link data-testid="footer-destinations-link" to="/destinations/" className="hover:text-brand-gold">Tourist Destinations</Link></li>
                            <li><Link data-testid="footer-contact-link" to="/contact" className="hover:text-brand-gold">Contact</Link></li>
                            <li><Link data-testid="footer-privacy-link" to="/privacy-policy" className="hover:text-brand-gold">Privacy Policy</Link></li>
                            <li><Link data-testid="footer-terms-link" to="/terms" className="hover:text-brand-gold">Booking Conditions</Link></li>
                            <li><Link data-testid="footer-refund-link" to="/refund-policy" className="hover:text-brand-gold">Refund Policy</Link></li>
                            <li><Link data-testid="footer-cancellation-link" to="/cancellation-policy" className="hover:text-brand-gold">Cancellation Policy</Link></li>
                            <li><a data-testid="footer-sitemap-link" href="/sitemap.xml" className="hover:text-brand-gold" target="_blank" rel="noreferrer">Sitemap</a></li>
                            <li><a href="/sitemap-en.xml" className="hover:text-brand-gold">English Sitemap</a></li>
                            <li><a href="/sitemap-hi.xml" className="hover:text-brand-gold">हिन्दी Sitemap</a></li>
                        </ul>
                    </div>

                    <div>
                        <h4 className="label-eyebrow text-brand-gold">
                            Popular Vehicles
                        </h4>
                        <ul className="mt-4 space-y-2 text-sm text-white/80">
                            {VEHICLES.map((v) => (
                                <li key={v.slug}>
                                    <Link
                                        data-testid={`footer-vehicle-${v.slug}-link`}
                                        to={`/${v.slug}`}
                                        className="hover:text-brand-gold"
                                    >
                                        {v.name}
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    </div>

                    <div>
                        <h4 className="label-eyebrow text-brand-gold">
                            Taxi Service Locations
                        </h4>
                        <ul className="mt-4 grid grid-cols-2 gap-x-3 gap-y-2 text-sm text-white/80">
                            {[...PRIORITY_CITY_SLUGS.map((s) => LOCATIONS.find((l) => l.slug === s)), ...LOCATIONS.filter((l) => !PRIORITY_CITY_SLUGS.includes(l.slug)).slice(0, 4)].filter(Boolean).map((l) => (
                                <li key={l.slug}>
                                    <Link
                                        data-testid={`footer-location-${l.slug}-link`}
                                        to={taxiCityPath(l.slug)}
                                        className="hover:text-brand-gold"
                                    >
                                        {l.name}
                                    </Link>
                                </li>
                            ))}
                        </ul>

                        <h4 className="label-eyebrow mt-6 text-brand-gold">
                            Tempo Traveller Rental
                        </h4>
                        <ul className="mt-4 grid grid-cols-2 gap-x-3 gap-y-2 text-sm text-white/80">
                            {TEMPO_CITY_SLUGS.map((s) => (
                                <li key={s}>
                                    <Link
                                        data-testid={`footer-tempo-location-${s}-link`}
                                        to={`/tempo-traveller-rental-${s}`}
                                        className="hover:text-brand-gold"
                                    >
                                        {LOCATIONS.find((location) => location.slug === s)?.name || s}
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>

                <div className="mt-12 flex flex-col items-start justify-between gap-4 border-t border-white/10 pt-6 text-xs text-white/60 sm:flex-row sm:items-center">
                    <div className="flex flex-col gap-1">
                        <div data-testid="footer-copyright">© {new Date().getFullYear()} VVPSS Private Limited Taxi Services. All rights reserved.</div>
                        <div data-testid="footer-service-coverage" className="text-white/50">GSTIN Registered · 24×7 support · Serving our listed locations across multiple states</div>
                        <div data-testid="footer-gst-notice" className="text-white/70">5% GST extra applies to all bookings. Listed rates exclude GST.</div>
                    </div>
                    {!informationOnly && <div className="flex flex-wrap items-center gap-3">
                        <button
                            data-testid="footer-whatsapp-button"
                            type="button"
                            onClick={() => openWhatsApp(config.whatsapp, defaultWhatsAppMessage(), "footer")}
                            className="inline-flex items-center gap-1.5 rounded-full bg-brand-whatsapp/15 px-3 py-1.5 font-semibold text-brand-whatsapp"
                        >
                            <WhatsAppIcon className="h-3.5 w-3.5" /> WhatsApp
                        </button>
                        <span data-testid="footer-gst-invoice-badge" className="rounded-full border border-white/15 px-3 py-1.5 font-semibold text-white/80">GST Invoice on Request</span>
                    </div>}
                </div>
                <FooterDeveloperCredit />
            </div>
        </footer>
    );
}
